function roundMoney(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100) / 100
}

export function packageCustomFeeLines(eventPackage) {
  const raw = Array.isArray(eventPackage?.customFees)
    ? eventPackage.customFees
    : eventPackage?.customFee
      ? [eventPackage.customFee]
      : []

  return raw
    .map((fee) => {
      const attrs =
        fee?.attributes && typeof fee.attributes === 'object'
          ? fee.attributes
          : fee
      const amount = Number(attrs?.feeAmount ?? attrs?.fee_amount)
      if (!Number.isFinite(amount) || amount <= 0) return null
      const name = String(attrs?.name || 'Custom fee').trim() || 'Custom fee'
      return {
        id: fee?.id ?? attrs?.id ?? name,
        name,
        option: String(attrs?.option || 'amount').toLowerCase(),
        amount,
      }
    })
    .filter(Boolean)
}

function customFeeDollars(line, percentBase) {
  if (line.option === 'percent') {
    return roundMoney(((Number(percentBase) || 0) * line.amount) / 100)
  }
  return roundMoney(line.amount)
}

function cardProcessingFee(amount) {
  const charge = Number(amount) || 0
  if (charge <= 0) return 0
  return roundMoney(0.3 + charge * 0.029)
}

/**
 * Fan-facing season package charge.
 * Ticket price stays the subtotal. The season package fee scales with seats.
 * Each custom fee is added once, then card processing is recalculated on that
 * total. Matches tickets/utils/package-custom-fee.js.
 */
export function previewPackageCharge({
  ticketSubtotal,
  seatCount = 1,
  seatFees,
  customFees,
}) {
  const seats = Math.max(0, Math.floor(Number(seatCount) || 0))
  const subtotal = roundMoney(ticketSubtotal)
  const fees = seatFees || {}
  const serviceFee =
    fees.serviceFeeMode === 'package-flat'
      ? roundMoney(fees.flatServiceFee)
      : roundMoney((Number(fees.serviceFeePerSeat) || 0) * seats)
  const tax = roundMoney((Number(fees.taxPerSeat) || 0) * seats)
  const facilityFee = roundMoney((Number(fees.facilityFeePerSeat) || 0) * seats)
  const chargebackProtectionFee = roundMoney(
    (Number(fees.chargebackProtectionFeePerSeat) || 0) * seats
  )
  // null means there is no per-seat org rule. Number(null) is 0, so a null
  // check has to happen before the numeric conversion or the card fee is dropped.
  const hasPerSeatProcessing =
    fees.processingFeePerSeat != null &&
    Number.isFinite(Number(fees.processingFeePerSeat))
  const orgProcessing = hasPerSeatProcessing
    ? roundMoney(Number(fees.processingFeePerSeat) * seats)
    : 0
  const beforeCustom = roundMoney(
    subtotal +
      serviceFee +
      tax +
      facilityFee +
      chargebackProtectionFee +
      orgProcessing
  )

  const customFeeLines = (Array.isArray(customFees) ? customFees : [])
    .map((line) => {
      const amount = customFeeDollars(line, beforeCustom)
      if (amount <= 0) return null
      return {
        id: line.id,
        name: line.name || 'Custom fee',
        amount,
      }
    })
    .filter(Boolean)

  const customFeeTotal = roundMoney(
    customFeeLines.reduce((sum, line) => sum + line.amount, 0)
  )

  let processingFee = orgProcessing
  if (!hasPerSeatProcessing && customFeeTotal <= 0) {
    processingFee = cardProcessingFee(subtotal)
  } else if (customFeeTotal > 0) {
    const baseWithoutProcessing = roundMoney(beforeCustom - orgProcessing)
    processingFee = cardProcessingFee(baseWithoutProcessing + customFeeTotal)
  }

  const total = roundMoney(
    subtotal +
      serviceFee +
      tax +
      facilityFee +
      chargebackProtectionFee +
      customFeeTotal +
      processingFee
  )

  return {
    subtotal,
    serviceFee,
    tax,
    facilityFee,
    chargebackProtectionFee,
    customFeeLines,
    customFeeTotal,
    processingFee,
    total,
  }
}

export function packageFeesAreAddedOnTop(eventPackage) {
  const fees = eventPackage?.websiteSeatFees
  return (
    packageCustomFeeLines(eventPackage).length > 0 ||
    Number(fees?.serviceFeePerSeat) > 0 ||
    Number(fees?.flatServiceFee) > 0 ||
    Number(fees?.taxPerSeat) > 0 ||
    Number(fees?.facilityFeePerSeat) > 0 ||
    Number(fees?.chargebackProtectionFeePerSeat) > 0
  )
}

export function packageAddedFeeLines(charge) {
  if (!charge) return []
  const lines = []
  if (charge.serviceFee > 0) {
    lines.push({
      id: 'season-package-fee',
      name: 'Season package fee',
      amount: charge.serviceFee,
    })
  }
  if (charge.facilityFee > 0) {
    lines.push({
      id: 'facility-fee',
      name: 'Facility fee',
      amount: charge.facilityFee,
    })
  }
  if (charge.tax > 0) {
    lines.push({ id: 'tax', name: 'Tax', amount: charge.tax })
  }
  if (charge.chargebackProtectionFee > 0) {
    lines.push({
      id: 'chargeback-protection',
      name: 'Chargeback protection',
      amount: charge.chargebackProtectionFee,
    })
  }
  for (const line of charge.customFeeLines || []) {
    lines.push(line)
  }
  return lines
}
